// Backend-First Email Service for SaaS Architecture
// API calls to secure server-side endpoints - keeps Resend API key secure

import reviewService from './reviewService';
import { createClient } from '@supabase/supabase-js';

class EmailService {
  constructor() {
    this.appUrl = import.meta.env.VITE_APP_URL || 'http://localhost:4028';
    this.supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    this.supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    // Initialize Supabase client for organization name fetching
    if (this.supabaseUrl && this.supabaseKey) {
      this.supabase = createClient(this.supabaseUrl, this.supabaseKey);
      console.log('✅ EmailService: Supabase client initialized');
    } else {
      console.warn('⚠️ EmailService: Supabase credentials missing');
    }
    
    // Fallback for development - remove in production
    this.isDevelopment = import.meta.env.MODE === 'development';
    this.hasDirectAccess = this.isDevelopment && import.meta.env.VITE_RESEND_API_KEY;
    
    if (this.hasDirectAccess) {
      console.warn('🚨 Development mode: Using direct Resend access. This will be disabled in production.');
      this.fromEmail = import.meta.env.VITE_FROM_EMAIL || 'noreply@reviews.servicereview.ai';
      
      // Initialize Resend promise for async loading
      this.resendPromise = import('resend').then(({ Resend }) => {
        console.log('🔧 Initializing Resend client...');
        this.resend = new Resend(import.meta.env.VITE_RESEND_API_KEY);
        console.log('✅ Resend client initialized');
        return this.resend;
      }).catch((error) => {
        console.error('❌ Resend initialization failed:', error);
        console.log('Resend not available - using backend API only');
        this.hasDirectAccess = false;
        return null;
      });
    }
  }

  // Ensure Resend is initialized before using it
  async ensureResendInitialized() {
    if (this.hasDirectAccess && this.resendPromise) {
      await this.resendPromise;
    }
    return this.resend;
  }

  // Generate secure review link with database token
  async generateReviewLink(reviewRequestId, customerId, customerEmail) {
    try {
      const tokenResult = await reviewService.createReviewToken(reviewRequestId, customerId, customerEmail);
      if (tokenResult.success) {
        return `${this.appUrl}/review-submission?token=${tokenResult.data.token}`;
      } else {
        console.error('Failed to create review token:', tokenResult.error);
        return null;
      }
    } catch (error) {
      console.error('Error generating review link:', error);
      return null;
    }
  }

  // Legacy method for backward compatibility - now delegates to reviewService
  verifyReviewToken(token) {
    // This method is now handled by reviewService.validateReviewToken()
    console.warn('verifyReviewToken is deprecated. Use reviewService.validateReviewToken() instead.');
    return { valid: false, error: 'Method deprecated' };
  }

  // Send review email (updated to use database tokens and campaign configuration)
  async sendReviewEmail({ customerEmail, customerName, reviewToken, appointmentData, replyToEmail = null, customerId = null, campaignConfig = null }) {
    try {
      console.log('📧 Attempting to send review email...');
      
      // Get current organization name for display
      let organizationName = 'ServiceReviewAI'; // fallback
      try {
        // Try to get organization name from the context or supabase
        const { data: { user } } = await this.supabase.auth.getUser();
        if (user) {
          const { data: membership } = await this.supabase
            .from('organization_members')
            .select('organization:organizations(name)')
            .eq('user_id', user.id)
            .single();
          
          if (membership?.organization?.name) {
            organizationName = membership.organization.name;
            console.log('🏢 Organization name fetched successfully:', organizationName);
          } else {
            console.warn('⚠️ No organization found in membership data:', membership);
          }
        } else {
          console.warn('⚠️ No user found for organization lookup');
        }
      } catch (orgError) {
        console.warn('Could not fetch organization name, using fallback:', orgError);
      }
      
      console.log('📝 Final organization name to send:', organizationName);
      
      // Try the working resend-email-sender function (has display name functionality)
      if (this.supabaseUrl) {
        console.log('🚀 Using resend-email-sender Edge Function (with display name)...');
        
        const response = await fetch(`${this.supabaseUrl}/functions/v1/resend-email-sender`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.supabaseKey}`,
          },
          body: JSON.stringify({
            customerId,
            customerEmail,
            customerName,
            reviewToken,
            replyToEmail,
            pipelineStage: appointmentData?.service_type || 'Service',
            campaignConfig: campaignConfig || {},
            organizationName: organizationName  // ✅ Pass the actual business name
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('✅ Resend-email-sender succeeded with display name');
          return result;
        } else {
          const errorResult = await response.json();
          console.error('resend-email-sender Edge Function error:', errorResult);
          
          // 🔄 Fallback to the old send-review-email function if needed
          console.log('🔄 Trying send-review-email as backup...');
          
          const backupResponse = await fetch(`${this.supabaseUrl}/functions/v1/send-review-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${this.supabaseKey}`,
            },
            body: JSON.stringify({
              customerId,
              customerEmail,
              customerName,
              reviewLink: `${this.appUrl}/review-submission?token=${reviewToken}`,
              serviceName: appointmentData?.service_type || 'Service',
              technicianName: 'Our team',
              serviceDate: appointmentData?.scheduled_date || new Date().toISOString(),
              campaignConfig: campaignConfig || {},
              replyToEmail: replyToEmail,
              businessSettings: {
                business_name: organizationName,
              }
            })
          });
          
          if (backupResponse.ok) {
            const backupResult = await backupResponse.json();
            console.log('✅ Backup function succeeded');
            return backupResult;
          } else {
            const backupError = await backupResponse.json();
            console.error('❌ Backup function also failed:', backupError);
            return { success: false, error: errorResult.error || 'Both email functions failed' };
          }
        }
      }
      
      // Fallback to direct method if no backend configured
      if (this.hasDirectAccess) {
        console.warn('🔄 Network error - falling back to direct method');
        return this.sendReviewEmailDirect({ customerEmail, customerName, reviewToken, appointmentData, replyToEmail, campaignConfig });
      }
      
      return { success: false, error: 'Direct email access not available in production' };
      
    } catch (error) {
      console.error('Error in sendReviewEmail:', error);
      
      // Try direct method as fallback in development
      if (this.hasDirectAccess) {
        console.warn('🔄 Network error - falling back to direct method');
        return this.sendReviewEmailDirect({ customerEmail, customerName, reviewToken, appointmentData, replyToEmail, campaignConfig });
      }
      
      return { success: false, error: 'Network error' };
    }
  }

  // Send review reminder email with token
  async sendReviewReminder(reviewRequestId, customerData, businessSettings = {}) {
    try {
      console.log('📧 Sending review reminder...');
      
      // Get review request with token
      const requestResult = await reviewService.getReviewRequestWithToken(reviewRequestId);
      if (!requestResult.success) {
        return { success: false, error: 'Review request not found' };
      }
      
      const reviewRequest = requestResult.data;
      const token = reviewRequest.review_tokens?.[0]?.token;
      
      if (!token) {
        return { success: false, error: 'No valid token found for review request' };
      }
      
      // Generate reminder email content
      const reviewLink = `${this.appUrl}/review-submission?token=${token}`;
      
      if (this.supabaseUrl) {
        const response = await fetch(`${this.supabaseUrl}/functions/v1/send-reminder-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.supabaseKey}`,
          },
          body: JSON.stringify({
            customerEmail: customerData.email,
            customerName: customerData.name,
            reviewLink: reviewLink,
            businessSettings: businessSettings
          })
        });

        if (response.ok) {
          const result = await response.json();
          // Mark reminder as sent
          await reviewService.markReminderSent(reviewRequestId);
          return result;
        } else {
          const errorResult = await response.json();
          console.error('Reminder API error:', errorResult);
          
          // Fall back to direct method in development
          if (this.hasDirectAccess) {
            console.warn('Falling back to direct reminder method');
            return this.sendReminderEmailDirect(customerData, reviewLink, businessSettings);
          }
          
          return { success: false, error: errorResult.error || 'Reminder API failed' };
        }
      }
      
      // Fallback to direct method if no backend configured
      if (this.hasDirectAccess) {
        const result = await this.sendReminderEmailDirect(customerData, reviewLink, businessSettings);
        if (result.success) {
          await reviewService.markReminderSent(reviewRequestId);
        }
        return result;
      }
      
      return { success: false, error: 'No email service configured' };
      
    } catch (error) {
      console.error('Error sending review reminder:', error);
      return { success: false, error: 'Network error' };
    }
  }

  // Send review request via backend API (preferred method)
  async sendReviewRequest(appointmentData, customerData, businessSettings = {}) {
    try {
      // Try backend API first
      if (this.supabaseUrl) {
        const response = await fetch(`${this.supabaseUrl}/functions/v1/send-review-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.supabaseKey}`,
          },
          body: JSON.stringify({
            appointmentId: appointmentData.id,
            customerEmail: customerData.email,
            businessSettings: businessSettings
          })
        });

        if (response.ok) {
          const result = await response.json();
          return result;
        } else {
          const errorResult = await response.json();
          console.error('Backend API error:', errorResult);
          
          // Fall back to direct method in development
          if (this.hasDirectAccess) {
            console.warn('Falling back to direct Resend method');
            return this.sendReviewRequestDirect(appointmentData, customerData, businessSettings);
          }
          
          return { success: false, error: errorResult.error || 'Backend API failed' };
        }
      }
      
      // Fallback to direct method if no backend configured
      if (this.hasDirectAccess) {
        return this.sendReviewRequestDirect(appointmentData, customerData, businessSettings);
      }
      
      return { success: false, error: 'No email service configured' };
      
    } catch (error) {
      console.error('Error sending review request:', error);
      
      // Try direct method as fallback in development
      if (this.hasDirectAccess) {
        console.warn('Network error - falling back to direct method');
        return this.sendReviewRequestDirect(appointmentData, customerData, businessSettings);
      }
      
      return { success: false, error: 'Network error' };
    }
  }

  // Test email functionality via backend API
  async sendTestEmail(testEmail) {
    try {
      if (this.supabaseUrl) {
        const response = await fetch(`${this.supabaseUrl}/functions/v1/send-test-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.supabaseKey}`,
          },
          body: JSON.stringify({ testEmail })
        });

        const result = await response.json();
        return result;
      }
      
      // Fallback for development
      if (this.hasDirectAccess) {
        return this.sendTestEmailDirect(testEmail);
      }
      
      return { success: false, error: 'No email service configured' };
      
    } catch (error) {
      console.error('Error sending test email:', error);
      return { success: false, error: 'Network error' };
    }
  }

  // ✅ NEW: Replace email template variables with actual values
  replaceEmailVariables(template, variables) {
    let result = template;
    
    // Replace common variables
    result = result.replace(/\{\{contact\.name\}\}/g, variables.customerName || '');
    result = result.replace(/\{\{business\.name\}\}/g, variables.businessName || 'ServiceReviewAI');
    result = result.replace(/\{\{service\.name\}\}/g, variables.serviceName || 'Service');
    
    return result;
  }

  // Direct Resend methods (development fallback only)
  
  // Direct review email method for development (with campaign configuration)
  async sendReviewEmailDirect({ customerEmail, customerName, reviewToken, appointmentData, replyToEmail = null, campaignConfig = null }) {
    console.log('🔧 Ensuring Resend is initialized...');
    const resend = await this.ensureResendInitialized();
    
    if (!resend) {
      console.error('❌ Resend not available');
      return { success: false, error: 'Resend not initialized' };
    }
    
    console.log('✅ Resend is ready, sending email...');

    try {
      const reviewLink = `${this.appUrl}/review-submission?token=${reviewToken}`;
      
      // ✅ NEW: Use campaign configuration if available
      const subject = campaignConfig?.email_subject 
        ? this.replaceEmailVariables(campaignConfig.email_subject, { customerName, businessName: 'ServiceReviewAI' })
        : `How was your ${appointmentData.service_type || 'service'}?`;
      
      const emailContent = this.renderReviewRequestEmail({
        customerName: customerName,
        businessName: 'ServiceReviewAI',
        serviceName: appointmentData.service_type || 'Service',
        technicianName: 'Our team',
        serviceDate: new Date(appointmentData.scheduled_date).toLocaleDateString(),
        reviewLink: reviewLink,
        businessLogo: null,
        // ✅ NEW: Pass campaign configuration to email renderer
        campaignConfig: campaignConfig
      });

      const emailOptions = {
        from: this.fromEmail,
        to: customerEmail,
        subject: subject,
        html: emailContent
      };

      // Add reply-to if provided
      if (replyToEmail) {
        emailOptions.reply_to = replyToEmail;
      }

      const { data, error } = await resend.emails.send(emailOptions);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, emailId: data.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async sendReviewRequestDirect(appointmentData, customerData, businessSettings = {}) {
    const resend = await this.ensureResendInitialized();
    if (!resend) {
      return { success: false, error: 'Resend not initialized' };
    }

    try {
      const reviewLink = this.generateReviewLink(appointmentData.id, customerData.id, customerData.email);
      
      const emailContent = this.renderReviewRequestEmail({
        customerName: customerData.name,
        businessName: businessSettings.business_name || 'ServiceReviewAI',
        serviceName: appointmentData.service?.name || 'Service',
        technicianName: appointmentData.technician?.full_name || 'Our technician',
        serviceDate: new Date(appointmentData.scheduled_start).toLocaleDateString(),
        reviewLink: reviewLink,
        businessLogo: businessSettings.business_logo_url
      });

      const { data, error } = await resend.emails.send({
        from: this.fromEmail,
        to: customerData.email,
        subject: `How was your ${appointmentData.service?.name || 'service'}?`,
        html: emailContent
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { 
        success: true, 
        emailId: data.id,
        reviewLink: reviewLink
      };
    } catch (error) {
      return { success: false, error: 'Failed to send email' };
    }
  }

  async sendTestEmailDirect(testEmail) {
    const resend = await this.ensureResendInitialized();
    if (!resend) {
      return { success: false, error: 'Resend not initialized' };
    }

    try {
      const { data, error } = await resend.emails.send({
        from: this.fromEmail,
        to: testEmail,
        subject: 'ServiceFlow Pro - Email Service Test (Direct)',
        html: `
          <h1>Email Service Test (Development Mode)</h1>
          <p>If you're seeing this, the direct email service is working!</p>
          <p>Timestamp: ${new Date().toISOString()}</p>
          <p><strong>Note:</strong> This uses direct Resend integration for development.</p>
        `
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, emailId: data.id };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // Email template rendering (used by direct methods) - now with campaign configuration
  renderReviewRequestEmail({ customerName, businessName, serviceName, technicianName, serviceDate, reviewLink, businessLogo, campaignConfig = null }) {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>How was your service?</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        ${businessLogo ? `<img src="${businessLogo}" alt="${businessName}" style="max-width: 200px; margin-bottom: 20px;">` : ''}
        
        <h1 style="color: #2563eb; font-size: 24px; margin-bottom: 20px;">How was our service?</h1>
        
        <p style="font-size: 16px; margin-bottom: 15px;">Hi ${customerName},</p>
        
        ${campaignConfig?.email_body_template 
          ? `<div style="font-size: 16px; margin-bottom: 25px; white-space: pre-line;">${this.replaceEmailVariables(campaignConfig.email_body_template, { customerName, businessName, serviceName })}</div>`
          : `
        <p style="font-size: 16px; margin-bottom: 15px;">
          Thank you for choosing ${businessName}! We hope you're satisfied with the ${serviceName} service 
          that ${technicianName} completed on ${serviceDate}.
        </p>
        
        <p style="font-size: 16px; margin-bottom: 25px;">
          We'd love to hear about your experience. Your feedback helps us improve our services and serve you better.
        </p>
          `
        }
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${reviewLink}" 
             style="background-color: #2563eb; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-size: 16px; font-weight: bold; display: inline-block;">
            ${campaignConfig?.cta_button_text || 'Share Your Experience'}
          </a>
        </div>
        
        <p style="font-size: 14px; color: #666; margin-bottom: 15px;">
          This review should only take 2-3 minutes of your time.
        </p>
        
        <div style="border-top: 1px solid #eee; padding-top: 20px; margin-top: 30px;">
          <p style="font-size: 14px; color: #666; margin-bottom: 10px;">
            <strong>Service Details:</strong><br>
            Service: ${serviceName}<br>
            Technician: ${technicianName}<br>
            Date: ${serviceDate}
          </p>
        </div>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #999;">
          <p>Thank you for choosing ${businessName}!</p>
          <p>If you have any questions, please don't hesitate to contact us.</p>
        </div>
      </body>
      </html>
    `;
  }

  // Get email statistics (placeholder - implement with backend API)
  async getEmailStatistics() {
    try {
      // This would call a backend API to get email statistics
      return {
        success: true,
        message: 'Email statistics available via backend API'
      };
    } catch (error) {
      return {
        success: false,
        error: 'Failed to fetch email statistics'
      };
    }
  }

  // Send reminder email directly (development fallback)
  async sendReminderEmailDirect(customerData, reviewLink, businessSettings = {}) {
    const resend = await this.ensureResendInitialized();
    if (!resend) {
      return { success: false, error: 'Resend not initialized' };
    }

    try {
      const emailContent = this.renderReminderEmail({
        customerName: customerData.name,
        businessName: businessSettings.business_name || 'ServiceReviewAI',
        reviewLink: reviewLink,
        businessLogo: businessSettings.business_logo_url
      });

      const { data, error } = await resend.emails.send({
        from: this.fromEmail,
        to: customerData.email,
        subject: 'Reminder: Share Your Experience',
        html: emailContent
      });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, emailId: data.id };
    } catch (error) {
      return { success: false, error: 'Failed to send reminder email' };
    }
  }

  // Render reminder email template
  renderReminderEmail({ customerName, businessName, reviewLink, businessLogo }) {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Reminder: Share Your Experience</title>
      </head>
      <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px;">
        ${businessLogo ? `<img src="${businessLogo}" alt="${businessName}" style="max-width: 200px; margin-bottom: 20px;">` : ''}
        
        <h1 style="color: #2563eb; font-size: 24px; margin-bottom: 20px;">We'd still love to hear from you!</h1>
        
        <p style="font-size: 16px; margin-bottom: 15px;">Hi ${customerName},</p>
        
        <p style="font-size: 16px; margin-bottom: 15px;">
          We recently reached out to get your feedback on our service, but we haven't heard back from you yet.
        </p>
        
        <p style="font-size: 16px; margin-bottom: 25px;">
          Your opinion matters to us! It only takes 2-3 minutes and helps us continue improving our services.
        </p>
        
        <div style="text-align: center; margin: 30px 0;">
          <a href="${reviewLink}" 
             style="background-color: #2563eb; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px; font-size: 16px; font-weight: bold; display: inline-block;">
            Share Your Experience
          </a>
        </div>
        
        <p style="font-size: 14px; color: #666; margin-bottom: 15px;">
          This is a friendly reminder - we won't send any more emails about this review.
        </p>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #999;">
          <p>Thank you for choosing ${businessName}!</p>
          <p>If you have any questions, please don't hesitate to contact us.</p>
        </div>
      </body>
      </html>
    `;
  }
}

export default new EmailService(); 